import {
  IRequirementResponse,
  RequirementExpirationStatus,
  RequirementStatus,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { flatten } from "lodash";
import React, { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { usePayeeQuery } from "../../../query/payee/queries/usePayeeQuery";
import { useInfiniteQueryRequirementsList } from "../../../query/requirements/queries/useInfiniteQueryRequirementsList";
import { useRequirementQuery } from "../../../query/requirements/queries/useRequirementQuery";
import {
  RequirementsListFilter,
  RequirementsListQuery
} from "../../../services/requirements/listRequirements";
import {
  UrlQuery,
  UrlQueryFilter,
  useUrlQuery
} from "../../../shared/hooks/useUrlQuery";
import { useSetWSStore } from "../../../store";
import { PayeeFilterRequirementStatus } from "../../Payees/routes/RoutePayees/filters/types";
import {
  getRequirementDefinitionIcon,
  getRequirementDefinitionName
} from "../../RequirementDefinitions/utils";
import { RequirementContextType } from "../../Requirements/types";
import { useRequirementsConfig } from "../../Requirements/useRequirementsConfig";
import { PayeeName } from "../components/PayeeName/PayeeName";
import { BulkReviewProps } from "../types";

interface RequirementsUrlQueryFilter extends UrlQueryFilter {
  requirementDefinitionId?: string;
  status?: PayeeFilterRequirementStatus;
}

export type RequirementsUrlQuery = UrlQuery<RequirementsUrlQueryFilter>;

function statusToRequest(
  value?: PayeeFilterRequirementStatus
): RequirementsListFilter {
  switch (value) {
    case PayeeFilterRequirementStatus.AwaitingContractorSignature:
      return {
        requirementType: RequirementType.Signature,
        "dataSourceMetadata.status": {
          in: ["New", "Sent"]
        }
      };
    case PayeeFilterRequirementStatus.AwaitingClientSignature:
      return {
        requirementType: RequirementType.Signature,
        "dataSourceMetadata.status": {
          in: ["Pending"]
        }
      };
    case PayeeFilterRequirementStatus.PendingBackgroundCheck:
      return {
        requirementType: RequirementType.BackgroundCheck,
        "dataSourceMetadata.status": {
          in: ["New", "Pending"]
        }
      };
    case PayeeFilterRequirementStatus.ProcessingBackgroundCheck:
      return {
        requirementType: RequirementType.BackgroundCheck,
        "dataSourceMetadata.status": {
          in: ["Processing"]
        }
      };
    case PayeeFilterRequirementStatus.FailedBackgroundCheck:
      return {
        requirementType: RequirementType.BackgroundCheck,
        "dataSourceMetadata.status": {
          in: ["Failed"]
        }
      };
    case PayeeFilterRequirementStatus.Incomplete:
      return {
        status: RequirementStatus.Incomplete
      };
    case PayeeFilterRequirementStatus.Complete:
      return {
        status: RequirementStatus.Complete
      };
    case PayeeFilterRequirementStatus.PendingApproval:
      return {
        status: RequirementStatus.PendingReview
      };
    case PayeeFilterRequirementStatus.Rejected:
      return {
        payerOverrideStatus: RequirementStatus.Incomplete
      };
    case PayeeFilterRequirementStatus.Approved:
      return {
        payerOverrideStatus: RequirementStatus.Complete
      };
    case PayeeFilterRequirementStatus.Expired:
      return {
        expirationStatus: RequirementExpirationStatus.Expired
      };
    case PayeeFilterRequirementStatus.Expiring:
      return {
        expirationStatus: RequirementExpirationStatus.Expiring
      };
    default:
      return {};
  }
}

const createListRequest = (
  urlQuery: RequirementsUrlQuery
): RequirementsListQuery => {
  return {
    filter: {
      ...statusToRequest(urlQuery.filter.status),
      requirementDefinitionId: urlQuery.filter.requirementDefinitionId,
      payerPayeeEngagementIds: { "!=": "null" }
    },
    sort:
      urlQuery.filter.status === PayeeFilterRequirementStatus.Expired
        ? {
            expirationDate: "desc"
          }
        : {
            updatedAt: "desc"
          }
  };
};

export function useBulkReviewRequirements(requirementId?: string) {
  const history = useHistory();
  const setStore = useSetWSStore();
  const location = useLocation();

  const { urlQuery, setUrlQuery } = useUrlQuery<RequirementsUrlQueryFilter>();

  const request = useMemo(() => createListRequest(urlQuery), [urlQuery]);
  const queryRequirements = useInfiniteQueryRequirementsList(request, {
    refetchOnMount: true
  });

  const requirements = useMemo(
    () => flatten(queryRequirements.data?.pages.map(page => page.data) || []),
    [queryRequirements.data?.pages]
  );

  const requirementsConfig = useRequirementsConfig({
    requirements,
    contextType: RequirementContextType.Payee
  });

  const requirementsListSize = useMemo(
    () => queryRequirements.data?.pages[0]?.summary?.listSize,
    [queryRequirements.data?.pages]
  );

  const bulkReviewProps = useMemo((): BulkReviewProps<IRequirementResponse> => {
    return {
      activeId: requirementId,
      listSize: requirementsListSize,
      rows:
        requirements.map(requirement => {
          const dataSourceContextType =
            requirementsConfig.getContextType(requirement);
          const dataSourcePolicyType =
            requirementsConfig.getPolicyType(requirement);

          return {
            id: requirement.requirementId,
            text: requirement.name || "–",
            secondaryText: requirement.payeeId
              ? React.createElement(PayeeName, {
                  payeeId: requirement.payeeId
                })
              : undefined,
            tertiaryText: getRequirementDefinitionName({
              type: requirement.requirementType,
              contextType: dataSourceContextType,
              policyType: dataSourcePolicyType
            }),
            tertiaryTextIcon: getRequirementDefinitionIcon({
              type: requirement.requirementType,
              contextType: dataSourceContextType,
              policyType: dataSourcePolicyType
            }),
            updatedAt: requirement.updatedAt,
            data: requirement
          };
        }) || [],
      onRowClick: row => {
        history.push({
          pathname: `/member/payees/${row.data.payeeId}/requirement/${row.id}`,
          search: location.search
        });
      },
      fetchNextPage: queryRequirements.fetchNextPage,
      hasNextPage:
        !queryRequirements.isFetchingNextPage && queryRequirements.hasNextPage,
      onClose: () => {
        setUrlQuery({
          filter: {}
        });
      }
    };
  }, [
    history,
    location.search,
    queryRequirements.fetchNextPage,
    queryRequirements.hasNextPage,
    queryRequirements.isFetchingNextPage,
    requirementId,
    requirements,
    requirementsListSize,
    setUrlQuery,
    requirementsConfig.dataUpdatedAt
  ]);

  // Handle bulk review visibility. Hide it on unmount
  useEffect(() => {
    const hasActiveFilters = Object.keys(urlQuery.filter).length > 0;
    const hasMultipleRequirements = requirements.length > 1;

    setStore({
      bulkReviewVisible: hasMultipleRequirements ? hasActiveFilters : false
    });

    return () => {
      setStore({
        bulkReviewVisible: false
      });
    };
  }, [requirements.length, setStore, urlQuery.filter]);

  const activeIndex = useMemo(
    () => requirements.findIndex(row => row.requirementId === requirementId),
    [requirements, requirementId]
  );

  const prevRequirement = useMemo(() => {
    if (activeIndex > 0) {
      return requirements[activeIndex - 1];
    }
    return undefined;
  }, [activeIndex, requirements]);

  const nextRequirement = useMemo(() => {
    if (activeIndex < requirements.length - 1) {
      return requirements[activeIndex + 1];
    }
    return undefined;
  }, [activeIndex, requirements]);

  // Prefetch requirements
  useRequirementQuery(prevRequirement?.requirementId!, {
    enabled: !!prevRequirement,
    staleTime: Infinity
  });
  useRequirementQuery(nextRequirement?.requirementId!, {
    enabled: !!nextRequirement,
    staleTime: Infinity
  });

  // Prefetch payees
  usePayeeQuery(prevRequirement?.payeeId!, {
    enabled: !!prevRequirement,
    staleTime: Infinity
  });
  usePayeeQuery(nextRequirement?.payeeId!, {
    enabled: !!nextRequirement,
    staleTime: Infinity
  });

  // Set bulk review data
  useEffect(() => {
    setStore({
      bulkReview: bulkReviewProps
    });
  }, [bulkReviewProps, setStore]);

  return {
    queryRequirements
  };
}
